import { IEmailPayload, ISession, ISignInPayload, ISignInResponse, IVerifyEmailPayload } from 'shared/models/Auth'
import { IServerUser, IUser } from 'shared/models/User'
import { ISignIn, IUserSignUp } from 'shared/utils/types/Auth.type'

import { IUpdateUserPayload } from 'features/profile/types'

import { BaseHttpService } from '../BaseHttpService'
import { QueryTypes } from '../data'
import { AuthConverter } from './AuthConverter'
import { IResetPasswordConfirm } from './types/types'

class AuthApi extends BaseHttpService {
  private converter

  private referral

  private pyramidUrl

  private usersUrl

  constructor(baseUrl: string) {
    super(baseUrl)
    this.baseUrl = `${baseUrl}/api/accounts`
    this.referral = `${baseUrl}/api/referral`
    this.pyramidUrl = `${baseUrl}/api/pyramid`
    this.usersUrl = `${baseUrl}/api/users`
    this.converter = new AuthConverter()
  }

  async logout(data: Pick<ISession, 'refresh'>) {
    return this.sendQuery<never, never, Pick<ISession, 'refresh'>>({
      url: `${this.baseUrl}/logout/`,
      type: QueryTypes.POST,
      data,
    })
  }

  async signIn(data: ISignInPayload) {
    return this.sendQuery<ISignInResponse, ISignIn, ISignInPayload>({
      url: `${this.baseUrl}/login/`,
      type: QueryTypes.POST,
      data,
      converterSuccess: this.converter.convertSignInData,
    })
  }

  async refreshSession(data: Pick<ISession, 'refresh'>) {
    return this.sendQuery<Pick<ISession, 'access'>, Pick<ISession, 'access'>, Pick<ISession, 'refresh'>>({
      url: `${this.baseUrl}/refresh/`,
      type: QueryTypes.POST,
      data,
    })
  }

  async passwordReset({ email }: IEmailPayload) {
    return this.sendQuery<string, string>({
      url: `${this.baseUrl}/password/reset/`,
      type: QueryTypes.POST,
      data: { email },
    })
  }

  async signUp(data: IUserSignUp) {
    return this.sendQuery({
      url: `${this.baseUrl}/registration/`,
      type: QueryTypes.POST,
      data,
    })
  }

  async verifyEmail({ token }: IVerifyEmailPayload) {
    return this.sendQuery({
      url: `${this.baseUrl}/email-verify/${token}`,
      type: QueryTypes.POST,
    })
  }

  async passwordResetConfirm({ token, ...data }: IResetPasswordConfirm) {
    return this.sendQuery<IResetPasswordConfirm, IResetPasswordConfirm>({
      url: `${this.baseUrl}/password/reset/confirm/${token}`,
      type: QueryTypes.POST,
      data,
    })
  }

  async getUserData() {
    return this.sendQuery<IServerUser, IUser>({
      url: `${this.baseUrl}/user`,
      type: QueryTypes.GET,
      converterSuccess: this.converter.convertUserData,
    })
  }

  async updateUserData(data: IUpdateUserPayload) {
    return this.sendQuery<IServerUser, IUser>({
      url: `${this.baseUrl}/user`,
      type: QueryTypes.PATCH,
      data,
      converterSuccess: this.converter.convertUserData,
    })
  }

  async resendEmail({ email }: IEmailPayload) {
    return this.sendQuery({
      url: `${this.baseUrl}/registration/resend-email/`,
      type: QueryTypes.POST,
      data: { email },
    })
  }

  async changeEmail({ email }: IEmailPayload) {
    return this.sendQuery({
      url: `${this.usersUrl}/change/user/email/`,
      type: QueryTypes.POST,
      data: { email },
    })
  }

  async getAgentLinks() {
    return this.sendQuery({
      url: `${this.pyramidUrl}/agent/get-socials/`,
      type: QueryTypes.GET,
      converterSuccess: this.converter.convertAgentLinks,
    })
  }

  async getGooglePopupAuthUrl(papData?: Record<string, string | number>) {
    return this.sendQuery<
      { auth_url: string; request_id: string },
      { authUrl: string; requestId: string },
      { pap_data?: Record<string, string | number> }
    >({
      url: `${this.baseUrl}/google/popup-start/`,
      type: QueryTypes.POST,
      data: {
        pap_data: papData,
      },
      converterSuccess: data => ({ authUrl: data.auth_url, requestId: data.request_id }),
    })
  }

  async getGooglePopupSession() {
    return this.sendQuery<{ authenticated: boolean; data?: ISignIn }, { authenticated: boolean; data?: ISignIn }>({
      url: `${this.baseUrl}/google/popup-session/`,
      type: QueryTypes.GET,
    })
  }

  async getGooglePopupResult(requestId: string) {
    return this.sendQuery<{ ready: boolean; payload?: any }, { ready: boolean; payload?: any }>({
      url: `${this.baseUrl}/google/popup-result/${requestId}/`,
      type: QueryTypes.GET,
    })
  }

  convertGooglePopupAuthPayload(data: ISignInResponse): ISignIn {
    return this.converter.convertSignInData(data)
  }
}

export default AuthApi
